import { useEffect, useRef, useState } from "react";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import './virtual_try_on.css'

const LANDMARKS = {
  LEFT_EYE_OUTER: 33,
  RIGHT_EYE_OUTER: 263,
};

const GLASSES_CONFIG = {
  "/spect2.png": {
    widthScale: 1.7,
    heightRatio: 0.5,
    offsetYFrac: 0.05,
  },
  "/spect3.png": {
    widthScale: 2.5,
    heightRatio: 0.55,
    offsetYFrac: 0.04,
  },
};

export default function Virtual_try_on({isOpen}) {
  const videoRef = useRef(null);

  const glassesList = Object.keys(GLASSES_CONFIG);
  const [selectedGlass, setSelectedGlass] = useState(glassesList[0]);

  const [glasses, setGlasses] = useState({
    visible: false,
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    angle: 0,
  });

  useEffect(() => {
    if(!isOpen){
        return;
    }

    let faceLandmarker = null;
    let animationId = null;
    let stream = null;

    const init = async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm",
        );

        faceLandmarker = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath:
              "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/latest/face_landmarker.task",
            delegate: "GPU",
          },
          runningMode: "VIDEO",
          numFaces: 1,
        });

        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "user",
            width: 1280,
            height: 720,
          },
        });

        if (!videoRef.current) return;

        videoRef.current.srcObject = stream;
        await videoRef.current.play();

        detectFace();
      } catch (error) {
        console.error(error);
      }
    };

    const detectFace = () => {
      if (!videoRef.current || !faceLandmarker) {
        animationId = requestAnimationFrame(detectFace);
        return;
      }

      const video = videoRef.current;
      const results = faceLandmarker.detectForVideo(video, performance.now());

      if (results.faceLandmarks?.length > 0) {
        const lm = results.faceLandmarks[0];

        const le = lm[LANDMARKS.LEFT_EYE_OUTER];
        const re = lm[LANDMARKS.RIGHT_EYE_OUTER];

        const cx = (le.x + re.x) / 2;
        const cy = (le.y + re.y) / 2;

        const angle = Math.atan2(re.y - le.y, re.x - le.x);

        const rect = video.getBoundingClientRect();
        const cfg = GLASSES_CONFIG[selectedGlass];

        const vw = video.videoWidth || rect.width;
        const vh = video.videoHeight || rect.height;

        // objectFit: "cover" crops the video — account for it
        const s = Math.max(rect.width / vw, rect.height / vh);
        const dw = vw * s;
        const dh = vh * s;

        // Screen position after mirror (scaleX(-1))
        const screenX = rect.width / 2 + dw * (0.5 - cx);
        const screenY = cy * dh - (dh - rect.height) / 2;

        // Glasses size from displayed-video face scale
        const eyeDist = Math.hypot(re.x - le.x, re.y - le.y);
        const frameW = eyeDist * dw * cfg.widthScale;
        const frameH = frameW * cfg.heightRatio;

        setGlasses({
          visible: true,
          left: screenX,
          top: screenY + frameH * cfg.offsetYFrac,
          width: frameW,
          height: frameH,
          angle,
        });
      } else {
        setGlasses((p) => ({ ...p, visible: false }));
      }

      animationId = requestAnimationFrame(detectFace);
    };

    init();

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, [selectedGlass,isOpen]);

  return (
    <div
      style={{
        width: "100vw",
        height: "100dvh",
        position: "relative",
        overflow: "hidden",
        background: "#000",
      }}
    >
      
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: "scaleX(-1)",
        }}
      />

      {glasses.visible && (
        <img
          src={selectedGlass}
          alt=""
          draggable={false}
          style={{
            position: "absolute",
            left: glasses.left,
            top: glasses.top,
            width: glasses.width,
            height: glasses.height,
            objectFit: "contain",
            transform: `translate(-50%, -50%) rotate(${-glasses.angle}rad)`,
            transformOrigin: "center center",
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 100,
          }}
        />
      )}

      <div
        style={{
          position: "absolute",
          bottom: 20,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          gap: "clamp(8px, 3vw, 16px)",
          zIndex: 200,
          flexWrap: "wrap",
          justifyContent: "center",
          padding: "0 10px",
        }}
      >
        {glassesList.map((item) => (
          <img
            key={item}
            src={item}
            alt=""
            onClick={() => setSelectedGlass(item)}
            style={{
              width: "clamp(50px, 18vw, 90px)",
              height: "clamp(28px, 9vw, 50px)",
              objectFit: "contain",
              cursor: "pointer",
              background: "#fff",
              padding: 5,
              borderRadius: 8,
              border:
                selectedGlass === item
                  ? "3px solid #00ff00"
                  : "2px solid rgba(255,255,255,0.5)",
              transition: "border 0.2s",
            }}
          />
        ))}
      </div>
    </div>
  );
}



export const Virtual_try_on_modal = ({ isOpen, onClose }) => {
    const dialogRef = useRef(null);
  
    useEffect(() => {
      const dialog = dialogRef.current;
  
      if (!dialog) return;
  
      if (isOpen && !dialog.open) {
        dialog.showModal();
      }
  
      if (!isOpen && dialog.open) {
        dialog.close();
      }
    }, [isOpen]);
  
    return (
      <dialog
        ref={dialogRef}
        className="virtual-tryon-dialog"
        onClose={onClose}
      >
        <button
          onClick={onClose}
          className="close-btn"
        >
          ✕
        </button>
  
      <Virtual_try_on  isOpen ={isOpen}/>
      </dialog>
    );
  };

